import {
  ReactNode,
  useEffect,
  useState,
} from 'react';

import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { shallowEqual, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import LoadingContent from 'app/modules/loading/LoadingContent';

import { formatDate, formatLongDateToDate } from 'app/services/dates';
import {
  LESSON_DATE_PARAM,
  fetchJoinLesson,
  creditAcceptsField,
  fetchMakeupCredits,
  fetchMarkLessonAbsence,
  fetchNextLessons,
  getLessonBranch,
  getLessonsPath,
} from 'app/services/lessons';

import LessonActionModal from './LessonActionModal';
import { getLessonInfo } from './LessonItem';

type LessonProps = {
  scheduleId: string,
}

type Action = {
  type: 'absence' | 'join',
  lesson: any,
}

const getHour = (dateTime: string) => dateTime?.substring(11, 16);

// "Segunda-feira, 02/11"
const formatWeekdayDate = (date: string) => {
  const weekday = format(parseISO(date), 'EEEE', { locale: ptBR });

  return weekday.charAt(0).toUpperCase() + weekday.slice(1) + ', ' + format(parseISO(date), 'dd/MM');
}

// "toda segunda-feira", "todo sábado"
const formatEveryWeekday = (date: string) => {
  const weekday = format(parseISO(date), 'EEEE', { locale: ptBR });

  return (['sábado', 'domingo'].includes(weekday) ? 'todo ' : 'toda ') + weekday;
}

// deadline to mark absence and receive the makeup credit: "hoje às 17:00", "amanhã às 17:00", "02/11 às 17:00"
const formatAbsenceUntil = (absenceUntil: string) => {
  const date = parseISO(absenceUntil);
  const day = isToday(date) ? 'hoje' : isTomorrow(date) ? 'amanhã' : format(date, 'dd/MM');

  return `${day} às ${format(date, 'HH:mm')}`;
}

// "encerrou hoje às 17:00", "encerrou em 30/10 às 17:00"
const formatPassedAbsenceUntil = (absenceUntil: string) => {
  return (isToday(parseISO(absenceUntil)) ? '' : 'em ') + formatAbsenceUntil(absenceUntil);
}

const isAbsenceDeadlinePassed = (lesson: any) => parseISO(lesson.attributes.absence_until) <= new Date();

// short status of an occurrence for the client
const getLessonStatus = (lesson: any) => {
  const attributes = lesson.attributes;

  if (attributes.is_absent) {
    return { label: 'Falta marcada', color: 'danger' };
  }

  if (attributes.is_participant) {
    return { label: 'Você participa', color: 'success' };
  }

  if (!attributes.has_available_spots) {
    return { label: 'Turma cheia', color: 'dark' };
  }

  return {
    label: `${attributes.available_spots} ${attributes.available_spots === 1 ? 'vaga livre' : 'vagas livres'}`,
    color: 'primary',
  };
}

const Lesson = ({ scheduleId }: LessonProps) => {
  const { auth } = useSelector((state: any) => ({
    auth: state.auth,
  }), shallowEqual);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [lessons, setLessons] = useState<any>([]);
  const [makeupCredits, setMakeupCredits] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [action, setAction] = useState<Action | null>(null);
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const selectedDate = searchParams.get(LESSON_DATE_PARAM);
  const lesson = lessons.find((lessonItem: any) => lessonItem.attributes.date === selectedDate) ?? lessons[0];
  const otherLessons = lessons.filter((lessonItem: any) => lessonItem !== lesson);
  const instructors = lesson?.relationships?.instructors ?? [];
  const fieldId = lesson?.relationships?.field?.id;
  const usableCredits = makeupCredits.filter((credit: any) => creditAcceptsField(credit, fieldId));
  const restrictedCredits = makeupCredits.filter((credit: any) => !creditAcceptsField(credit, fieldId));
  const restrictedFields = Array.from(new Set(restrictedCredits.flatMap((credit: any) =>
    (credit.relationships?.fields ?? []).map((field: any) => field.attributes.name)
  ))).join(', ');
  const branch = getLessonBranch(lesson);

  // goes back to where the client came from (grid, contract...), otherwise to the branch lessons grid
  const goBack = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }

    navigate(getLessonsPath(branch?.attributes?.slug));
  }

  const getLessons = () => {
    return fetchNextLessons(scheduleId).then((data: any) => {
      setLessons(data);
      setNotFound(false);

      return data;
    }).catch(() => {
      setLessons([]);
      setNotFound(true);

      return [];
    });
  }

  const getMakeupCredits = (data: any) => {
    if (!auth.user || !data?.[0]?.attributes?.accepts_makeup_class) {
      setMakeupCredits([]);
      return;
    }

    fetchMakeupCredits(data[0])
      .then((credits: any) => setMakeupCredits(credits))
      .catch(() => setMakeupCredits([]));
  }

  const refresh = () => getLessons().then((data: any) => getMakeupCredits(data));

  useEffect(() => {
    setLoading(true);

    refresh().finally(() => setLoading(false));
  }, [scheduleId, auth?.user?.id]);

  // a date that is not one of the next occurrences is removed from the url, the first occurrence is shown
  useEffect(() => {
    if (loading || !selectedDate || lessons.some((lessonItem: any) => lessonItem.attributes.date === selectedDate)) {
      return;
    }

    searchParams.delete(LESSON_DATE_PARAM);
    setSearchParams(searchParams, { replace: true });
  }, [loading, lessons, selectedDate]);

  const selectLesson = (lessonItem: any) => {
    setSearchParams({ [LESSON_DATE_PARAM]: lessonItem.attributes.date }, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const requireLogin = () => {
    if (!auth.user) {
      navigate({}, { state: { login: location } });
      return true;
    }

    if (!auth.user?.phone) {
      navigate({}, { state: { verifyPhoneUrl: location } });
      return true;
    }

    return false;
  }

  const openJoin = (lessonItem: any) => {
    if (requireLogin()) {
      return;
    }

    setAction({ type: 'join', lesson: lessonItem });
  }

  const confirmAction = () => {
    if (!action) {
      return;
    }

    const date = action.lesson.attributes.date;

    setActionLoading(true);

    const request = action.type === 'absence'
      ? fetchMarkLessonAbsence(scheduleId, date)
      : fetchJoinLesson(scheduleId, date, usableCredits[0].id);

    request.then(() => {
      toast.success(action.type === 'absence'
        ? 'Falta marcada! Seu crédito de reposição já está disponível.'
        : 'Pronto! Você está na aula.');

      setAction(null);

      return refresh();
    }).catch(() => {
      // error already notified
    }).finally(() => {
      setActionLoading(false);
    });
  }

  const renderActions = (lessonItem: any) => {
    const attributes = lessonItem.attributes;

    if (attributes.can_mark_absence) {
      return <div className='pt-4'>
        <div className='fs-7 text-gray-600 pb-3'>
          Não vai poder ir? Marque falta até <b>{formatAbsenceUntil(attributes.absence_until)}</b> para
          {attributes.uses_makeup_credit ? ' receber o seu crédito de reposição de volta.' : ' receber um crédito de reposição.'}
        </div>

        <span
          className='btn btn-sm btn-light-danger'
          onClick={() => setAction({ type: 'absence', lesson: lessonItem })}
        >
          Marcar falta
        </span>
      </div>;
    }

    if (attributes.is_participant && !attributes.is_absent) {
      return <div className='fs-7 text-gray-600 pt-4'>
        O prazo para marcar falta nesta aula encerrou <b>{formatPassedAbsenceUntil(attributes.absence_until)}</b>, por isso não é mais possível
        {attributes.uses_makeup_credit ? ' receber o crédito de volta.' : ' receber crédito de reposição.'}
      </div>;
    }

    // absent students can come back to the lesson using a makeup credit
    if ((attributes.is_participant && !attributes.is_absent) || !attributes.accepts_makeup_class) {
      return null;
    }

    if (!attributes.has_available_spots) {
      return <div className='fs-7 text-gray-600 pt-4'>
        Turma cheia, não há vaga para reposição nesta data.
      </div>;
    }

    if (auth.user && usableCredits.length === 0 && restrictedCredits.length > 0) {
      return <div className='fs-7 text-gray-600 pt-4'>
        Seu crédito de reposição não vale para esta quadra{restrictedFields && <>, apenas para: <b>{restrictedFields}</b></>}.
      </div>;
    }

    if (auth.user && (!attributes.can_use_makeup_credit || usableCredits.length === 0)) {
      return <div className='fs-7 text-gray-600 pt-4'>
        Você não possui crédito de reposição disponível para esta aula.
      </div>;
    }

    return <div className='pt-4'>
      {attributes.is_absent && <div className='fs-7 text-gray-600 pb-3'>
        Mudou de ideia? Você pode voltar para esta aula usando um crédito de reposição.
      </div>}

      {isAbsenceDeadlinePassed(lessonItem) && <div className='fs-7 text-warning pb-3'>
        O prazo para marcar falta nesta aula encerrou {formatPassedAbsenceUntil(attributes.absence_until)}: o crédito usado não poderá ser devolvido.
      </div>}

      <span
        className='btn btn-sm btn-primary'
        onClick={() => openJoin(lessonItem)}
      >
        {!auth.user && 'Entrar para usar crédito de reposição'}
        {auth.user && (attributes.is_absent ? 'Voltar para a aula com crédito de reposição' : 'Entrar com crédito de reposição')}
      </span>
    </div>;
  }

  const renderDetail = (icon: string, label: string, value: ReactNode) => (
    <div className='col-6 col-md-3'>
      <div className='border border-gray-300 border-dashed rounded h-100 py-3 px-4'>
        <div className='d-flex align-items-center text-gray-600 fs-7 fw-semibold mb-1'>
          <i className={`las ${icon} fs-3 me-1`} />
          {label}
        </div>
        <div className='fs-6 fw-bold text-gray-800'>
          {value}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <span
        className='btn btn-md btn-icon btn-bg-light btn-active-color-primary w-100px h-40px cursor-pointer mb-5'
        onClick={goBack}
      >
        <i className='las la-arrow-left fw-bold fs-2 text-primary me-3' />
        Voltar
      </span>

      {loading && <LoadingContent />}

      {!loading && (notFound || !lesson) && <div className='card mb-5 mb-xl-10'>
        <div className='card-body my-10 text-center'>
          <h2 className='mb-3'>
            {notFound ? 'Ops, aula não encontrada!' : 'Nenhuma aula programada'}
          </h2>
          <div className='text-muted fw-semibold fs-5'>
            {notFound ? 'Verifique o link ou volte para a grade de aulas.' : 'Esta turma não tem aulas nas próximas semanas.'}
          </div>
        </div>
      </div>}

      {!loading && lesson && <>
        <div className='card mb-5'>
          <div className='card-body p-6 p-lg-10'>
            {branch?.attributes?.name && <div className='text-primary fw-bold fs-7 text-uppercase mb-2'>
              {branch.attributes.name}
            </div>}

            <h1 className='mb-1'>
              Aula em turma • {lesson.relationships.field.attributes.name}
            </h1>

            <div className='fs-3 fw-bold text-gray-800 mb-4'>
              {formatWeekdayDate(lesson.attributes.date)} • {getHour(lesson.attributes.start_at)} - {getHour(lesson.attributes.finish_at)}
            </div>

            <div className='d-flex flex-wrap gap-2 mb-6'>
              <span className={`badge badge-light-${getLessonStatus(lesson).color} fs-7`}>
                {getLessonStatus(lesson).label}
              </span>

              {lesson.attributes.uses_makeup_credit && <span className='badge badge-light-warning fs-7'>
                Usando crédito de reposição
              </span>}

              {getLessonInfo(lesson).map((info: string) => <span key={`info-${info}`} className='badge badge-light fs-7'>
                {info}
              </span>)}

              {lesson.attributes.accepts_makeup_class
                ? <span className='badge badge-light-primary fs-7'>Aceita reposição</span>
                : <span className='badge badge-light fs-7'>Não aceita reposição</span>}
            </div>

            <div className='fs-6 text-gray-700 mb-6'>
              Aula em grupo com professor{instructors.length > 0 && <> <b>{instructors.map((instructor: any) => instructor.attributes.name).join(', ')}</b></>}
              {branch?.attributes?.name && <> na unidade <b>{branch.attributes.name}</b></>}.
              {lesson.attributes.is_recurrence && <> A turma se encontra <b>{formatEveryWeekday(lesson.attributes.date)}</b>, das {getHour(lesson.attributes.start_at)} às {getHour(lesson.attributes.finish_at)}.</>}
              {lesson.attributes.accepts_makeup_class
                ? <> Alunos que faltaram em outra aula podem usar um <b>crédito de reposição</b> para participar desta aula, se houver vaga.</>
                : <> Esta aula não aceita reposição.</>}
            </div>

            <div className='row g-3 mb-6'>
              {renderDetail('la-clock', 'Duração', `${lesson.attributes.duration} minutos`)}
              {renderDetail('la-user-friends', 'Jogadores', `${lesson.attributes.occupied_spots} de ${lesson.attributes.spots}`)}
              {renderDetail('la-door-open', 'Vagas livres', lesson.attributes.has_available_spots ? lesson.attributes.available_spots : 'Turma cheia')}
              {renderDetail('la-chalkboard-teacher', 'Professor', instructors.length > 0
                ? instructors.map((instructor: any) => instructor.attributes.name).join(', ')
                : 'A definir')}
            </div>

            {lesson.attributes.accepts_makeup_class && auth.user && makeupCredits.length > 0 && <div className='notice d-flex flex-column bg-light-primary rounded border-primary border border-dashed p-6 mb-2'>
              {usableCredits.length > 0 && <div className='fs-6 text-gray-800'>
                Você tem <b>{usableCredits.length}</b> {usableCredits.length === 1 ? 'crédito' : 'créditos'} de reposição para esta aula.
                {usableCredits[0].attributes.expired_at && <> O próximo vence em <b>{formatLongDateToDate(usableCredits[0].attributes.expired_at)}</b>.</>}
              </div>}

              {restrictedCredits.length > 0 && <div className={`fs-6 text-gray-800 ${usableCredits.length > 0 ? 'pt-2' : ''}`}>
                {usableCredits.length > 0 ? 'Você também tem' : 'Você tem'} <b>{restrictedCredits.length}</b> {restrictedCredits.length === 1 ? 'crédito' : 'créditos'} de reposição que não {restrictedCredits.length === 1 ? 'vale' : 'valem'} para esta quadra
                {restrictedFields && <>, apenas para: <b>{restrictedFields}</b></>}.
              </div>}
            </div>}

            {renderActions(lesson)}
          </div>
        </div>

        {otherLessons.length > 0 && <div className='card mb-5 mb-xl-10'>
          <div className='card-body p-6 p-lg-10'>
            <h3 className='mb-1'>
              Outras datas desta turma
            </h3>
            <div className='text-muted fw-semibold fs-7 mb-5'>
              Toque em uma data para ver os detalhes e as ações disponíveis.
            </div>

            <div className='row g-4'>
              {otherLessons.map((lessonItem: any) => <div key={`lesson-${lessonItem.attributes.start_at}`} className='col-12 col-md-4'>
                <div
                  className='card card-dashed h-100 p-5 cursor-pointer bg-hover-light'
                  onClick={() => selectLesson(lessonItem)}
                >
                  <div className='text-primary fw-bold fs-6'>
                    {formatDate(lessonItem.attributes.date)}
                  </div>

                  <div className='fs-3 fw-bold text-gray-800'>
                    {getHour(lessonItem.attributes.start_at)} - {getHour(lessonItem.attributes.finish_at)}
                  </div>

                  <div className='d-flex flex-stack mt-3'>
                    <span className={`fs-7 fw-bold text-${getLessonStatus(lessonItem).color}`}>
                      {getLessonStatus(lessonItem).label}
                    </span>

                    <span className='fs-7 fw-semibold text-primary'>
                      Ver aula <i className='las la-angle-right text-primary' />
                    </span>
                  </div>
                </div>
              </div>)}
            </div>
          </div>
        </div>}
      </>}

      <LessonActionModal
        isOpen={action?.type === 'absence'}
        title='Marcar falta'
        variant='danger'
        button='Marcar falta'
        isLoading={actionLoading}
        onClosed={() => setAction(null)}
        onConfirm={confirmAction}
        text={action && <>
          <p>
            Você vai marcar falta na aula de <b>{formatDate(action.lesson.attributes.date)}</b> e sua vaga ficará livre.
          </p>
          {action?.lesson?.attributes?.uses_makeup_credit ? <p className='mb-0'>
            Como você entrou nesta aula usando um <b>crédito de reposição</b>, ele será devolvido para usar em outra aula com vaga.
            O crédito é <b>válido por 30 dias</b>.
          </p> : <p className='mb-0'>
            Você receberá um <b>crédito de reposição de aula</b> para entrar em outra aula com vaga.
            O crédito é <b>válido por 30 dias</b>.
          </p>}
        </>}
      />

      <LessonActionModal
        isOpen={action?.type === 'join'}
        title={action?.lesson?.attributes?.is_absent ? 'Voltar para a aula' : 'Entrar na aula'}
        button='Usar crédito'
        isLoading={actionLoading}
        onClosed={() => setAction(null)}
        onConfirm={confirmAction}
        text={action && <>
          <p>
            {action.lesson.attributes.is_absent ? 'Você marcou falta, mas vai voltar para' : 'Você vai entrar na'} aula de <b>{formatDate(action.lesson.attributes.date)}</b> usando <b>1 crédito de reposição</b>.
          </p>
          {isAbsenceDeadlinePassed(action.lesson) ? <div className='notice bg-light-warning rounded border-warning border border-dashed p-4'>
            <div className='fw-bold text-gray-800 mb-1'>Este crédito não é reembolsável</div>
            <div className='text-gray-700'>
              O prazo para marcar falta nesta aula encerrou <b>{formatPassedAbsenceUntil(action.lesson.attributes.absence_until)}</b>.
              Se entrar agora, você não poderá marcar falta nem receber o crédito de volta.
            </div>
          </div> : <p className='mb-0'>
            Se não puder ir, marque falta até <b>{formatAbsenceUntil(action.lesson.attributes.absence_until)}</b> para receber o crédito de volta.
          </p>}
        </>}
      />
    </>
  );
}

export {
  Lesson,
}
